import React, { Fragment, useState, useEffect } from "react";
import { ToolIcon, HorizontalToolbar, Badge, SearchMenu, Divider } from "@theorgchartdev/storybook";
import { useLocation } from "react-router-dom";
import ExportMenu from "./ExportMenu/ExportMenu";
import HelpMenu from "./HelpMenu/HelpMenu";
import UserSettingsMenu from "./UserSettingsMenu/UserSettingsMenu";
import { useWSMgrContext } from "@/providers/WsMgrProvider";
import { LeftHeaderChartControls } from "@/appComponents/Navigation/Header/LeftHeaderControls/LeftHeaderChartControls";
import { LeftHeaderReportControls } from "@/appComponents/Navigation/Header/LeftHeaderControls/LeftHeaderReportControls";
import WebLinkMenu from "@/appComponents/Navigation/Header/WebLinkMenu/WebLinkMenu";
import DisplayToggle from "@/appComponents/Blocks/Utils/DisplayToggle/DisplayToggle";
import {
  enumDataSource,
  kAdminUser,
  kRWUser,
  kROUser,
  enumUIModes,
  enumUISubModes,
  enumFeaturesList,
  enumBrand
} from "@coreapp/constants";
import { gShowHelpReadOnly, gWhiteLabel } from "@coreapp/whitelabel/brand";
import { Globals } from "@coreapp/CoreApp/Globals";
import { HeaderNavIcon } from "@/appComponents/Navigation/MainNavigation/HeaderNavIcon";
import HeaderSettingsMenu from "@/appComponents/Navigation/MainNavigation/HeaderSettingsMenu";
import { appstr } from "@coreapp/utils/appstr";
import { getFeatureAccess } from "@/Utils/Misc";
import { OCLink } from "@/appComponents/Blocks/Utils/OCLink";
import { usePlanManagerContext } from "@/providers/PlanManagerProvider";
import OnRampBanner from "./OnRampBanner/OnRampBanner";

const uiComponents = {
  chart: {
    showQuickAccess: true,
    showAvatar: true,
    showChartSelector: true,
    showFileName: true,
    showDescription: true,
    showExport: true,
    showHelp: true,
    showLink: true,
    showSync: true,
    showAutoSave: true,
    showSettingsMenu: true
  },

  reports: {
    showQuickAccess: true,
    showAvatar: true,
    showExport: true,
    hideChartControls: true,
    showHelp: true,
    showReportSelector: true,
    showSettingsMenu: true
  },

  settings: {
    showAvatar: true,
    showHelp: true,
    showSettingsMenu: true
  },

  layoutEditor: {
    showAvatar: true,
    showChartSelector: true,
    showQuickAccess: true,
    showFileName: true,
    showDescription: true,
    showExport: true,
    showLink: true,
    showHelp: true,
    showAutoSave: true,
    showSettingsMenu: true
  }
};

const ToolbarDivider = () => (
  <div className={"ocn-toolbar-horizontal-divider"}>
    <Divider vertical={true} />
  </div>
);

function Header(props) {
  const ctx = useWSMgrContext();
  const wsMgr = ctx.wsMgr.state;
  const docMgr = wsMgr?.GetDocMgr();
  const doc = docMgr?.GetCurrentDoc();

  const isArchive =
    docMgr?.GetIWizDataInteractive()?.GetDataSource() === enumDataSource.pointintime;
  const nMgr = wsMgr ? wsMgr.GetNotificationMgr() : null;
  const unreadMessagesCount = nMgr ? nMgr.GetUnreadMessagesCount() : 0;

  const canRefresh = () => {
    if ([kRWUser].includes(Globals._accountInfo.GetUserRole())) {
      const isCustomChart = !(doc?.IsServerChart() ?? true);
      return Globals._uiConfig.fRWUsersCanRefresh || isCustomChart;
    }
    if ([kAdminUser].includes(Globals._accountInfo.GetUserRole())) {
      return true;
    } else return false;
  };

  const { planManager } = usePlanManagerContext();

  const isEmbeddedMode = Globals._uiConfig?.fEmbeddedMode ?? false;

  if (isEmbeddedMode) {
    return null;
  }

  const planApprovalsEnabled = planManager?.state?.GetApprovalSettings()?.enablePlanApprovals;

  const isReadWriteUser = [kAdminUser, kRWUser].includes(Globals._accountInfo.GetUserRole());
  const PermissionCheck = (key, value) => {
    const uiMode = Globals._uiConfig.GetUIMode();
    switch (key) {
      case "showChartSelector":
        return Globals._uiConfig.fChartExplorer && getFeatureAccess(enumFeaturesList.chartExplorer);
      case "showFileName":
        return true;
      case "showDescription":
        return Globals._uiConfig.fEnableAbout;
      case "showSync":
      case "showAutoSave":
        return doc && isReadWriteUser && canRefresh() && Globals._uiConfig.fChartExplorer;
      case "showQuickAccess":
        return Globals._uiConfig.fChartExplorer || Globals._uiConfig.InBoxLayoutEditorMode();
      case "showLink":
        return !isArchive && isReadWriteUser && Globals._uiConfig.fWebLinks;
      case "showExport":
        return Globals._uiConfig.fCanPublish;
      case "showHelp": {
        const isReadOnly = Globals._accountInfo.GetUserRole() === kROUser;
        return Globals._uiConfig.fEnableHelp && isReadOnly && gShowHelpReadOnly;
      }
      case "showAvatar":
        return Globals._uiConfig.fShowCurrentUser && gWhiteLabel !== enumBrand.isolved;
      case "showSettingsMenu":
        return gWhiteLabel === enumBrand.isolved;
      case "showDisplayToggle":
        return uiMode === enumUIModes.planning && doc?.IsPositionDocument();
      case "showSubmissionStatus":
        return uiMode === enumUIModes.planning && planApprovalsEnabled;
      default:
        return value;
    }
  };

  const GetComponentsShown = () => {
    let modeAccess = {};

    if (Globals._uiConfig.InBackgroundEditorMode()) {
      modeAccess = { ...uiComponents.layoutEditor };
      modeAccess.showSync = true;
      modeAccess.showAutoSave = true;
    } else {
      const uiMode = Globals._uiConfig.GetUIMode();
      switch (uiMode) {
        case enumUIModes.chart:
        case enumUIModes.edit:
        case enumUIModes.directory:
          modeAccess = uiComponents.chart;
          break;
        case enumUIModes.planning:
          modeAccess = { ...uiComponents.chart };
          modeAccess.showDisplayToggle = true;
          modeAccess.showSubmissionStatus = false;
          break;
        case enumUIModes.details:
        case enumUIModes.settings:
          modeAccess = uiComponents.settings;
          break;
        case enumUIModes.boxlayouteditor:
          modeAccess = uiComponents.layoutEditor;
          break;
        case enumUIModes.reports:
          modeAccess = uiComponents.reports;
          break;
      }
    }

    let access = {};
    Object.entries(modeAccess).map(([key, value]) => (access[key] = PermissionCheck(key, value)));
    return access;
  };

  const componentAccess = GetComponentsShown();

  // normalized chart options
  const leftContent = (
    <Fragment>
      <LeftHeaderChartControls
        showChartSelector={componentAccess.showChartSelector}
        showDescription={componentAccess.showDescription}
        showLink={componentAccess.showLink}
        showSync={componentAccess.showSync}
        showAutoSave={componentAccess.showAutoSave}
        hideChartControls={componentAccess.hideChartControls}
        showFileName={componentAccess.showFileName}
        showQuickAccess={componentAccess.showQuickAccess}
        showSubmissionStatus={componentAccess.showSubmissionStatus}
      />
      {componentAccess.showReportSelector && <LeftHeaderReportControls />}
    </Fragment>
  );

  const centerContent = (
    <Fragment>
      {componentAccess.showGeneralSearch && (
        <span className={"ocn-search-wrapper"} id={"toolbar-search-general-container"}>
          <SearchMenu id={"toolbar-search-general"} options={[]} />
        </span>
      )}
    </Fragment>
  );

  const rightContent = (
    <Fragment>
      {componentAccess.showDisplayToggle && (
        <>
          <DisplayToggle />
          <ToolbarDivider />
        </>
      )}
      <OnRampBanner />
      {componentAccess.showHelp && <HelpMenu />}
      {componentAccess.showNotification && (
        <Fragment>
          <Badge
            showBadge={false}
            badgeContent={unreadMessagesCount}
            color="error"
            maxLength={3}
            overlap="circular"
            pin="top-right"
            size="medium"
          >
            <ToolIcon name={"bell"} title={appstr("Notifications")} size="medium" disabled={true} />
          </Badge>
        </Fragment>
      )}
      <HeaderNavIcon itemKey="pit" />
      <HeaderNavIcon itemKey="reports" />
      {componentAccess.showSettingsMenu && <HeaderSettingsMenu />}
      {componentAccess.showSettingsMenu && componentAccess.showExport && <ToolbarDivider />}
      {componentAccess.showAvatar && <UserSettingsMenu divider={componentAccess.showExport} />}
      {componentAccess.showLink && <WebLinkMenu />}
      {componentAccess.showExport && <ExportMenu />}
    </Fragment>
  );

  return (
    <header className="header" aria-label="app header" role="complementary">
      <HorizontalToolbar
        leftContent={leftContent}
        rightContent={rightContent}
        centerContent={centerContent}
        size={"large"}
        {...props}
      />
    </header>
  );
}

export default Header;
